import { HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical } from 'lucide-react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'
import { VisualScreen } from 'app/frontend/types/custom/VisualPlan'
import VisualCanvas from '../VisualCanvas'

type ScreenCardProps = {
  screen: VisualScreen
  encounterId: number
  index: number
}

const ScreenCard = ({ screen, encounterId, index }: ScreenCardProps) => {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: screen.id })

  return (
    <Card
      ref={setNodeRef}
      className={cn('p-2 cursor-grab select-none', isDragging && 'opacity-60')}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
      }}
      {...(attributes as HTMLAttributes<HTMLDivElement>)}
      {...(listeners as HTMLAttributes<HTMLDivElement>)}
    >
      <VisualCanvas
        encounterId={encounterId}
        imageIndex={screen.image_index}
        shapes={screen.shapes}
        zoom={screen.zoom}
        crop={screen.crop}
        showZoomOverview={false}
        showText={false}
        className='w-full overflow-hidden rounded'
      />
      <div className='mt-1 flex items-center justify-between gap-1'>
        <span className='truncate text-sm font-medium'>
          {t('dashboard.team.plans.visuals.screen', { index })}
        </span>
        <GripVertical className='h-4 w-4 shrink-0 text-muted-foreground' />
      </div>
    </Card>
  )
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  encounterId: number
  screens: VisualScreen[]
  onReorder: (screens: VisualScreen[]) => void
}

const ScreenReorderDialog = ({
  open,
  onOpenChange,
  encounterId,
  screens,
  onReorder,
}: Props) => {
  const { t } = useTranslation()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const sourceIndex = screens.findIndex((s) => s.id === String(active.id))
    const targetIndex = screens.findIndex((s) => s.id === String(over.id))
    if (sourceIndex < 0 || targetIndex < 0) return
    onReorder(arrayMove(screens, sourceIndex, targetIndex))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.plans.visuals.reorder-screens')}
          </DialogTitle>
        </DialogHeader>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={screens.map((s) => s.id)}
            strategy={rectSortingStrategy}
          >
            <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
              {screens.map((screen, index) => (
                <ScreenCard
                  key={screen.id}
                  screen={screen}
                  encounterId={encounterId}
                  index={index + 1}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t('shared.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ScreenReorderDialog
